import React, { useState } from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

import { useTranslation } from '../../src/hooks/useTranslation';
import { ROUTES } from '../../src/navigation/routes';
import { Language } from '../../src/i18n';
import { brand } from '../../src/design-system/theme/tokens';

// Same Professional Design Tokens used across the rest of the funnel.
// Keeping these inline (vs. importing) makes each onboarding screen
// independently auditable for visual coherence.
const COLORS = {
  primary: brand.primary,
  background: '#FFFFFF',
  surface: '#F9FAFB',
  border: '#E5E7EB',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
};

export default function LanguageSelectScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { changeLanguage, availableLanguages } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('en');
  const [isChanging, setIsChanging] = useState(false);

  const handleContinue = async () => {
    if (isChanging) return;
    setIsChanging(true);
    try {
      await changeLanguage(selectedLanguage);
      router.replace(ROUTES.PUBLIC_LOGIN as any);
    } catch (error) {
      console.error('Failed to change language:', error);
      setIsChanging(false);
    }
  };

  return (
    <SafeAreaView className="flex-1 bg-surface" style={{ backgroundColor: COLORS.surface }}>
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          paddingHorizontal: 24,
          paddingTop: 40,
          paddingBottom: Math.max(insets.bottom + 40, 60),
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View className="w-full max-w-2xl mx-auto">
          <Card className="mb-6">
            <CardContent className="p-6 md:p-8">
              {/* Icon block + headline (mockup pattern) */}
              <View className="flex-row items-center mb-4">
                <View
                  className="w-12 h-12 rounded-2xl items-center justify-center mr-3"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  <MaterialIcons name="language" size={24} color="white" />
                </View>
                <Text
                  className="text-2xl font-bold flex-1"
                  style={{ color: COLORS.text.primary }}
                >
                  Select Language
                </Text>
              </View>
              <Text
                className="text-base leading-6 mb-6"
                style={{ color: COLORS.text.secondary }}
              >
                Choose your preferred language. You can change this later from your profile.
              </Text>

              {/* Language options */}
              <View style={{ gap: 12 }}>
                {availableLanguages.map((lang) => {
                  const isSelected = selectedLanguage === lang.code;
                  return (
                    <TouchableOpacity
                      key={lang.code}
                      onPress={() => setSelectedLanguage(lang.code)}
                      activeOpacity={0.7}
                      className="flex-row items-center p-4 rounded-lg"
                      style={{
                        backgroundColor: isSelected
                          ? `${COLORS.primary}10`
                          : COLORS.surface,
                        borderWidth: 2,
                        borderColor: isSelected ? COLORS.primary : COLORS.border,
                      }}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: isSelected }}
                    >
                      <View className="flex-1">
                        <Text
                          className="text-base font-semibold mb-0.5"
                          style={{ color: COLORS.text.primary }}
                        >
                          {lang.nativeName}
                        </Text>
                        <Text className="text-sm" style={{ color: COLORS.text.secondary }}>
                          {lang.name}
                        </Text>
                      </View>
                      {isSelected && (
                        <MaterialIcons
                          name="check-circle"
                          size={22}
                          color={COLORS.primary}
                        />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>

              {/* Continue button — primary blue, matches rest of funnel */}
              <Button
                variant="default"
                size="default"
                onPress={handleContinue}
                disabled={isChanging}
                className="mt-6"
              >
                {isChanging ? (
                  <View className="flex-row items-center">
                    <ActivityIndicator size="small" color="white" />
                    <Text className="text-white font-semibold ml-2">
                      Setting Language…
                    </Text>
                  </View>
                ) : (
                  <Text className="text-white font-semibold">Continue</Text>
                )}
              </Button>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
